import { REFRESH_TOKEN_MUTATION } from '@/graphql'
import { ApolloLink, from, HttpLink, NextLink, Operation } from '@apollo/client/core'
import { onError } from '@apollo/client/link/error'
import { fromPromise } from 'apollo-link'
import Cookies from 'js-cookie'
import router from '@/router'
import { client } from '@/vue-apollo'
import { getRefreshToken, getToken, setTokens } from '@/helpers/authHelper'

// Add authorization header
const httpLink = new HttpLink({
  uri: () => process.env.VUE_APP_API_PATH || 'http://localhost:4000',
})

// Add headers
const traceMiddleware = new ApolloLink((operation: Operation, forward: NextLink) => {
  if (operation.operationName) { // -- If the grqphQL request has a defined name
    operation.setContext({ // -- Override default trace descriptor
      headers: {
        'x-envoy-decorator-operation': `Apollo - ${operation.operationName}`,
        authorization: getToken() || null,
      }
    })
  }

  return forward(operation)
})

const tryRefreshTokens = async (): Promise<boolean> => {
  const refreshToken = getRefreshToken()
  if (refreshToken) {
    // Attempt to fetch new token and refreshToken
    const resp = await client.mutate({
      mutation: REFRESH_TOKEN_MUTATION,
      variables: {
        refreshToken: refreshToken
      }
    })
    if (resp.errors) {
      // Refresh token is not valid
      return false
    }
    const nextToken = resp?.data?.refreshToken.token
    const nextRefreshToken = resp?.data?.refreshToken.refreshToken
    if (nextToken && nextRefreshToken) {
      // New token and refresh token acquired
      setTokens(nextToken, nextRefreshToken)
      return true
    }
  }
  return false
}

let isRefreshing = false
let pendingRequests: (() => Promise<void>)[] = []

const resolvePendingRequests = () => {
  pendingRequests.map(callback => callback())
  pendingRequests = []
}

const errorLink = onError(
  ({ graphQLErrors, operation, forward }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        if (err?.extensions?.code === 'UNAUTHENTICATED') {
          let forwards

          if (!isRefreshing) {
            isRefreshing = true
            forwards = fromPromise(
              tryRefreshTokens()
                // Refresh was successful
                .then((refreshed) => {
                  // Update auth header for failed request now that tokens are refreshed
                  operation.setContext({ headers: { authorization: getToken() } })
                  resolvePendingRequests()
                  return refreshed
                })
                // Could not refresh tokens, go to login
                .catch(() => {
                  pendingRequests = []
                  router.push({ name: 'Login' })
                })
                .finally(() => {
                  isRefreshing = false
                })
            ).filter(value => Boolean(value))
          } else {
            // Will only emit once the Promise is resolved
            forwards = fromPromise(
              new Promise<void>(resolve => {
                pendingRequests.push(async () => resolve())
              })
            )
          }
          return forwards.flatMap(() => forward(operation))
        }
      }
    }
  }
)

export const link = from([traceMiddleware, errorLink, httpLink])
